import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  Typography,
  Box,
  Autocomplete,
  IconButton,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { TOUCH, FONT_SIZE, FONT_WEIGHT, ZINDEX } from '@/core/platform/platformStandards';
import GavelIcon from '@mui/icons-material/Gavel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import agreementService from '@/services/agreementService';
import lookupService from '@/services/lookupService';
import AgreementPdfViewer, {
  AgreementBusyState,
  openAgreementPdfInNewTab,
} from '@/components/agreements/AgreementPdfViewer';
import {
  agreementModalPaperSx,
  agreementModalTitleSx,
  agreementModalContentSx,
  agreementPdfRegionSx,
  agreementModalFooterStripSx,
} from '@/components/agreements/agreementModalLayout';

const AGREEMENT_LABELS = {
  revenue_share: 'Revenue Share Agreement',
  subscription: 'Subscription Agreement',
};

const PDF_TYPE_MAP = {
  revenue_share: 'revshare',
  subscription: 'subscription',
};

const SUBMIT_MESSAGES = {
  revenue_share: 'Recording your signature and applying it to your agreement…',
  subscription: 'Recording your signature…',
};

const ESignatureModal = ({
  open,
  onClose,
  onSign,
  isLoading,
  firmData,
  userData,
  agreementType,
  authMode = 'authenticated',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [fullName, setFullName] = useState('');
  const [title, setTitle] = useState(null);
  const [jobTitles, setJobTitles] = useState([]);
  const [loadingTitles, setLoadingTitles] = useState(false);
  const [titlesLoadError, setTitlesLoadError] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [draftS3Key, setDraftS3Key] = useState(null);
  const [pdfContentHash, setPdfContentHash] = useState('');
  const [agreementVersion, setAgreementVersion] = useState('1.0');
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [viewerInstanceKey, setViewerInstanceKey] = useState(0);
  const pdfViewSessionRef = useRef('');
  const wasSubmittingRef = useRef(false);

  const agreementLabel = AGREEMENT_LABELS[agreementType] ?? AGREEMENT_LABELS.revenue_share;
  const pdfType = PDF_TYPE_MAP[agreementType] || 'revshare';

  const usePersonalized = useMemo(
    () =>
      authMode === 'authenticated' &&
      agreementType === 'revenue_share' &&
      pdfType === 'revshare',
    [authMode, agreementType, pdfType]
  );

  const pdfLoadingMessage = usePersonalized
    ? 'Personalizing your agreement with your firm name and address…'
    : 'Loading agreement…';

  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    setError('');
    setIsSubmitting(false);
    setSubmitMessage('');
    setPdfUrl(null);
    setDraftS3Key(null);
    setPdfContentHash('');
    setPdfError('');

    let cancelled = false;

    const loadPdf = async () => {
      try {
        setLoadingPdf(true);
        const res = usePersonalized
          ? await agreementService.getAgreementPersonalizedPdfUrl(pdfType)
          : authMode === 'public'
            ? await agreementService.getPublicAgreementPdfUrl(pdfType)
            : await agreementService.getAgreementPdfUrl(pdfType);

        if (cancelled) {
          return;
        }

        if (res?.url) {
          if (usePersonalized && res.personalized === false) {
            throw new Error(
              'Personalized agreement PDF was not returned. Please refresh and try again.'
            );
          }
          setPdfUrl(res.url);
          setDraftS3Key(res.draft_s3_key || null);
          setPdfContentHash(res.content_hash || '');
          setAgreementVersion(res.agreement_version || '1.0');
        } else {
          const endpoint = usePersonalized
            ? `/fetch/agreement_url?type=${pdfType}&personalized=true`
            : authMode === 'public'
              ? `/public/agreement_url?type=${pdfType}`
              : `/fetch/agreement_url?type=${pdfType}`;
          setPdfError(`Agreement PDF URL was not returned (${endpoint}).`);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Agreement PDF load failed:', err);
          setPdfError(err.message || 'Failed to load agreement PDF.');
        }
      } finally {
        if (!cancelled) {
          setLoadingPdf(false);
        }
      }
    };

    const loadTitles = async () => {
      try {
        setLoadingTitles(true);
        setTitlesLoadError('');
        await lookupService.waitForInitialization();
        let titles = lookupService.getOptions('job_titles') || [];
        if (!titles.length) {
          titles = await lookupService.getJobTitles();
        }
        if (cancelled) {
          return;
        }
        const normalized = (titles || []).map((t) => ({
          value: t.value,
          display: t.display || t.label || t.value,
          label: t.label || t.display || t.value,
        }));
        if (!normalized.length) {
          setTitlesLoadError('Job titles could not be loaded. Refresh the page or contact support.');
        }
        setJobTitles(normalized);
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading job titles:', err);
          setTitlesLoadError(err.message || 'Failed to load job titles.');
          setJobTitles([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingTitles(false);
        }
      }
    };

    loadPdf();
    loadTitles();

    return () => {
      cancelled = true;
    };
  }, [open, pdfType, authMode, usePersonalized]);

  useEffect(() => {
    if (!open) {
      pdfViewSessionRef.current = '';
      return;
    }
    if (!pdfUrl || loadingPdf) {
      return;
    }
    if (pdfViewSessionRef.current === pdfUrl) {
      return;
    }
    pdfViewSessionRef.current = pdfUrl;
    setViewerInstanceKey((key) => key + 1);
  }, [open, pdfUrl, loadingPdf]);

  useEffect(() => {
    if (wasSubmittingRef.current && !isSubmitting && open && pdfUrl && !loadingPdf) {
      setViewerInstanceKey((key) => key + 1);
    }
    wasSubmittingRef.current = isSubmitting;
  }, [isSubmitting, open, pdfUrl, loadingPdf]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setFullName(userData?.fullName || '');
  }, [open, userData?.fullName]);

  useEffect(() => {
    if (!open || !userData?.jobTitle || !jobTitles.length) return;
    const match = jobTitles.find(
      (t) =>
        t.value === userData.jobTitle ||
        t.display === userData.jobTitle ||
        t.label === userData.jobTitle
    );
    if (match) setTitle(match);
  }, [open, userData?.jobTitle, jobTitles]);

  const validate = () => {
    const name = fullName.trim();
    if (!name) return 'Please enter your full name';
    if (name.split(/\s+/).length < 2) return 'Please enter your full name (first and last name)';
    if (!/^[a-zA-Z\s'-]+$/.test(name)) {
      return 'Name can only contain letters, spaces, hyphens, and apostrophes';
    }
    if (!title) return 'Please select your title';
    return '';
  };

  const handleSign = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!pdfContentHash) {
      setError('Agreement PDF hash is unavailable. Please reload and try again.');
      return;
    }
    setIsSubmitting(true);
    setError('');
    setSubmitMessage(SUBMIT_MESSAGES[agreementType] || SUBMIT_MESSAGES.revenue_share);

    const reportSubmitStatus = (message) => {
      if (message) {
        setSubmitMessage(message);
      }
    };

    try {
      let ipAddress = 'unknown';
      try {
        const ipResponse = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipResponse.json();
        ipAddress = ipData.ip;
      } catch {
        console.warn('Could not fetch IP address');
      }

      const signedAt = new Date();
      const titleStr = title.display || title.label || title.value || '';
      const signatureAppendix = [
        '',
        '---',
        'ELECTRONIC SIGNATURE',
        `Signed by: ${fullName.trim()}`,
        `Title: ${titleStr}`,
        `Date: ${signedAt.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`,
        `Timestamp (UTC): ${signedAt.toISOString()}`,
        `IP Address: ${ipAddress}`,
        `Agreement Version: ${agreementVersion}`,
      ].join('\n');

      await onSign({
        fullName: fullName.trim(),
        title: title.value || titleStr,
        titleDisplay: titleStr,
        timestamp: signedAt.toISOString(),
        ipAddress,
        userAgent: navigator.userAgent,
        agreementType,
        agreementVersion,
        draftS3Key,
        pdfContentHash,
        filledAgreementText: signatureAppendix,
        reportSubmitStatus,
      });
    } catch (err) {
      setError(err?.message || 'Failed to submit signature. Please try again.');
      console.error('Error submitting signature:', err);
    } finally {
      setIsSubmitting(false);
      setSubmitMessage('');
    }
  };

  const pdfLoadFailed = !loadingPdf && !pdfUrl;
  const viewerError = pdfLoadFailed
    ? pdfError || 'Agreement PDF could not be loaded.'
    : '';

  const canSign =
    fullName.trim() && title && pdfUrl && pdfContentHash && !isSubmitting && !isLoading && !loadingPdf;

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      sx={{ zIndex: ZINDEX.modal }}
      PaperProps={{
        sx: agreementModalPaperSx(isMobile, theme),
      }}
    >
      <DialogTitle sx={agreementModalTitleSx}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="space-between"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
            <GavelIcon sx={{ mr: 1, color: 'primary.main', flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: FONT_WEIGHT.semibold, lineHeight: 1.3 }}>
                {agreementLabel}
              </Typography>
              {firmData?.firmName && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {firmData.firmName}
                </Typography>
              )}
            </Box>
          </Box>

          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
            {pdfUrl && !isSubmitting && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                onClick={() => openAgreementPdfInNewTab(pdfUrl)}
                disabled={loadingPdf}
                sx={{
                  minHeight: TOUCH.minSize,
                  display: { xs: 'none', sm: 'inline-flex' },
                }}
              >
                Open in new tab
              </Button>
            )}
            <IconButton
              onClick={onClose}
              disabled={isSubmitting}
              aria-label="Close"
              sx={{ minWidth: TOUCH.minSize, minHeight: TOUCH.minSize }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={agreementModalContentSx}>
        {isSubmitting ? (
          <AgreementBusyState message={submitMessage} minHeight="100%" size={32} />
        ) : isLoading ? (
          <AgreementBusyState message="Loading…" minHeight="100%" />
        ) : (
          <>
            <Box sx={agreementPdfRegionSx}>
              <AgreementPdfViewer
                pdfUrl={pdfUrl}
                isLoading={loadingPdf}
                loadingMessage={pdfLoadingMessage}
                error={viewerError}
                title={agreementLabel}
                minHeight="100%"
                viewerInstanceKey={viewerInstanceKey}
                active={open && !isSubmitting && !isLoading}
              />
            </Box>

            <Box sx={agreementModalFooterStripSx}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1, fontWeight: FONT_WEIGHT.medium }}
              >
                Electronic signature *
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: '1fr 1fr auto',
                    md: '1.2fr 1fr auto',
                  },
                  gap: 1,
                  alignItems: 'start',
                }}
              >
                <TextField
                  size="small"
                  label="Full legal name"
                  placeholder="First and last name"
                  value={fullName}
                  onChange={(e) => {
                    setFullName(e.target.value);
                    if (error) setError('');
                  }}
                  error={!!error && error.includes('name')}
                  disabled={isSubmitting}
                  required
                  sx={{
                    '& .MuiInputBase-input': {
                      fontFamily: 'cursive',
                      fontSize: FONT_SIZE.md,
                      color: 'primary.main',
                    },
                  }}
                />
                <Autocomplete
                  size="small"
                  value={title}
                  onChange={(_, v) => {
                    setTitle(v);
                    if (error) setError('');
                  }}
                  options={jobTitles}
                  getOptionLabel={(opt) =>
                    typeof opt === 'string' ? opt : opt?.display || opt?.label || opt?.value || ''
                  }
                  isOptionEqualToValue={(a, b) => a?.value === b?.value}
                  loading={loadingTitles}
                  disabled={isSubmitting || loadingTitles}
                  disablePortal
                  slotProps={{
                    popper: {
                      sx: { zIndex: ZINDEX.modal + 150 },
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Title"
                      required
                      error={!!error && error.includes('title')}
                      helperText={
                        titlesLoadError ||
                        (error && error.includes('title') ? error : undefined)
                      }
                      placeholder={loadingTitles ? 'Loading titles…' : 'Select title'}
                    />
                  )}
                />
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    minHeight: 40,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minWidth: { sm: 96 },
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Date
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: FONT_WEIGHT.semibold, fontSize: FONT_SIZE.sm }}>
                    {currentDate}
                  </Typography>
                </Box>
              </Box>

              {error && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75 }}>
                  {error}
                </Typography>
              )}

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1, lineHeight: 1.4 }}
              >
                By clicking Sign Agreement, you agree to use an electronic signature with the same
                legal effect as a handwritten signature on this {agreementLabel}.
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ mt: 1.25 }}
              >
                {pdfUrl ? (
                  <Button
                    onClick={() => openAgreementPdfInNewTab(pdfUrl)}
                    disabled={loadingPdf}
                    variant="outlined"
                    startIcon={<OpenInNewIcon />}
                    sx={{
                      minHeight: TOUCH.minSize,
                      display: { xs: 'inline-flex', sm: 'none' },
                    }}
                  >
                    Open in new tab
                  </Button>
                ) : (
                  <Box sx={{ display: { xs: 'none', sm: 'block' } }} />
                )}

                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="flex-end"
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{ minHeight: TOUCH.minSize, flex: { xs: 1, sm: 'none' } }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSign}
                    disabled={!canSign}
                    variant="contained"
                    startIcon={<CheckCircleIcon />}
                    sx={{ minHeight: TOUCH.minSize, minWidth: { sm: 132 }, flex: { xs: 1, sm: 'none' } }}
                  >
                    Sign Agreement
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

ESignatureModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSign: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  agreementType: PropTypes.oneOf(['revenue_share', 'subscription']),
  authMode: PropTypes.oneOf(['public', 'authenticated']),
  firmData: PropTypes.shape({
    firmName: PropTypes.string,
    firmAddress: PropTypes.string,
    firmEmail: PropTypes.string,
  }),
  userData: PropTypes.shape({
    fullName: PropTypes.string,
    jobTitle: PropTypes.string,
  }),
};

ESignatureModal.defaultProps = {
  isLoading: false,
  agreementType: 'revenue_share',
  authMode: 'authenticated',
  firmData: null,
  userData: null,
};

export default ESignatureModal;
